import { getTransactionsEndpoint } from './auditSources';

const name = value => String(value || '').normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Nazwy projektów, które miały zapisy w poprzednim roku.
 *
 * Reguła `project_pnl_summary` odróżnia nimi projekt rozpoczęty w tym roku od projektu
 * ciągnącego się z poprzedniego (decyzja ownera 11.10.2026: karta pokazuje projekty na minusie
 * tylko wtedy, gdy zaczęły się w tym roku albo mają obrót nad progiem 5G). Sam odczyt przez
 * proxy RAPPORTERA, bez zapisu i bez modeli. Brak danych nie zatrzymuje audytu: reguła dostaje
 * `status: 'unavailable'` i pokazuje wszystkie projekty na minusie.
 *
 * @returns {Promise<{status:'available'|'unavailable', year:number, names:string[], reason?:string}>}
 */
export async function fetchPriorYearProjects({ fetchFn, source, request, transactions, isAborted = () => false }) {
    const year = Number(request?.year) - 1;
    const unavailable = reason => ({ status: 'unavailable', year, names: [], reason });
    const endpoint = getTransactionsEndpoint(source);
    if (!endpoint) return unavailable('source_without_transactions');
    if (!Array.isArray(transactions) || !transactions.some(tx => tx?.projectName)) return unavailable('no_projects_in_period');
    if (isAborted()) return unavailable('aborted');
    try {
        // Poprzedni rok rzadko się zmienia: bez `force`, żeby RAPPORTER mógł oddać go z pamięci 24 h
        // zamiast ciągnąć całą księgę z 24SO przy każdym audycie.
        const body = JSON.stringify({ ...request, year, monthFrom: 1, month: 12, force: false });
        const response = await fetchFn(endpoint, body, { isAborted });
        const mismatch = (field, expected) => response?.[field] != null && Number(response[field]) !== expected;
        if (!response || typeof response !== 'object' || !Array.isArray(response.transactions)
            || mismatch('year', year) || mismatch('startMonth', 1) || mismatch('endMonth', 12)
            || (name(request.companyName) && name(response.client) && name(response.client) !== name(request.companyName))
            // Ta sama bramka co prepareApiAuditData: niepełna księga nie jest listą projektów.
            || (['tripletex','unimicro'].includes(source) && (response.complete !== true || response.source !== source))) {
            return unavailable('source_mismatch');
        }
        const names = [...new Set(response.transactions.map(tx => tx?.projectName).filter(Boolean).map(String))];
        const projects=[...new Map(response.transactions.filter(tx=>tx?.projectId && tx?.projectName).map(tx=>[String(tx.projectId),{projectId:String(tx.projectId),name:String(tx.projectName)}])).values()];
        const completeIds=response.transactions.filter(tx=>tx?.projectName).every(tx=>tx.projectId);
        return { status: 'available', year, names, projects, identityComplete:completeIds, count: response.transactions.length };
    } catch (e) {
        return unavailable(e?.code || e?.message || 'fetch_failed');
    }
}
